import { GetTariffsPaginatedModel } from '@delmonicos/shared-sdk/libs/cpms/src/apps/charge-point-operators-gateway/src/tariffs/models/inputs/get-tariffs-paginated.model';
import { useUrlSyncedFilters } from '@delmonicos/shared-sdk/libs/cpms/src/clients/utils/hooks';
import { CreateTariffModel } from '@delmonicos/shared-sdk/libs/cpms/src/libs/charge-hub-service-lib/src/wrapper/inputs/tariff/create-tariff.model';
import { DeleteTariffModel } from '@delmonicos/shared-sdk/libs/cpms/src/libs/charge-hub-service-lib/src/wrapper/inputs/tariff/delete-tariff.model';
import { UpdateTariffModel } from '@delmonicos/shared-sdk/libs/cpms/src/libs/charge-hub-service-lib/src/wrapper/inputs/tariff/update-tariff.model';
import { PriceComponentModel } from '@delmonicos/shared-sdk/libs/cpms/src/libs/charge-hub-service-lib/src/wrapper/outputs/tariff/price-component.model';
import { TariffModel } from '@delmonicos/shared-sdk/libs/cpms/src/libs/charge-hub-service-lib/src/wrapper/outputs/tariff/tariff.model';
import { DayOfWeek } from '@delmonicos/shared-sdk/libs/cpms/src/libs/ocpi/src/enums/ocpi-protocol-enums';
import {
  Dispatch,
  SetStateAction,
  createContext,
  useContext,
  useMemo,
  useState,
} from 'react';
import { DeepPartialSkipArrayKey } from 'react-hook-form';

import {
  TABLE_LIMITS_OPTIONS,
  TABLE_LIMIT_OPTION_MIN,
} from '@app/config/app.config';
import {
  TARIFF_FILTERS_LSK,
  TARIFF_LIST_OPTIONS_LSK,
} from '@app/config/localstorage-keys.const';
import { useLocalStorageOptions } from '@shared/hooks/useLocalStorageOptions';

import {
  TARIFF_LIST_DEFAULT_OPTIONS,
  TARIFF_LIST_FILTER_NAME_LIST,
  TARIFF_LIST_SORT_FIELD,
  TARIFF_LIST_SORT_ORDER,
  TariffListOptionsModel,
} from './tariff-list/tariff-list.config';

// /////////////////////////////////////////////////////////////////////////////

export const TariffContext = createContext<TariffModel | null>(null);

export const useTariffContext = (): TariffModel => {
  const context = useContext(TariffContext);

  if (!context) {
    throw new Error(
      'useTariffContext must be used within TariffContext.Provider',
    );
  }

  return context;
};

interface TariffContextProviderProps {
  data: TariffModel;
  children: React.ReactNode;
}

export const TariffContextProvider = ({
  data,
  children,
}: TariffContextProviderProps): React.JSX.Element => {
  return (
    <TariffContext.Provider value={data}>{children}</TariffContext.Provider>
  );
};

// /////////////////////////////////////////////////////////////////////////////

export interface TariffListContextProps {
  getPaginatedQueryParameters: GetTariffsPaginatedModel;
  setGetPaginatedQueryParameters: Dispatch<
    SetStateAction<GetTariffsPaginatedModel>
  >;
  getPaginatedLimitList: number[];
  columnOptions: TariffListOptionsModel;
  setColumnOptions: Dispatch<SetStateAction<TariffListOptionsModel>>;
  // setGetPaginatedLimitList: Dispatch<SetStateAction<number>>;
}

export const TariffListContext = createContext<TariffListContextProps | null>(
  null,
);

export const useTariffListContext = (): TariffListContextProps => {
  const context = useContext(TariffListContext);

  if (!context) {
    throw new Error(
      'useTariffListContext must be used within TariffListContext.Provider',
    );
  }

  return context;
};

interface TariffListContextProviderProps {
  children: React.ReactNode;
}

export const TariffListContextProvider = ({
  children,
}: TariffListContextProviderProps): React.JSX.Element => {
  const [getPaginatedQueryParameters, setGetPaginatedQueryParameters] =
    useUrlSyncedFilters<GetTariffsPaginatedModel>(
      {
        // Pagination
        limit: TABLE_LIMITS_OPTIONS.at(-1) ?? TABLE_LIMIT_OPTION_MIN,
        offset: 0,
        // Filterable fields
        type: undefined,
        textToSearch: '',
        // Sortable fields
        sortField: TARIFF_LIST_SORT_FIELD,
        sortOrder: TARIFF_LIST_SORT_ORDER,
      },
      [
        'limit',
        'offset',
        'sortField',
        'sortOrder',
        'textToSearch',
        ...TARIFF_LIST_FILTER_NAME_LIST,
      ],
      TARIFF_FILTERS_LSK,
    );

  const [columnOptions, setColumnOptions] =
    useLocalStorageOptions<TariffListOptionsModel>(
      TARIFF_LIST_DEFAULT_OPTIONS,
      TARIFF_LIST_OPTIONS_LSK,
    );

  const data = useMemo(() => {
    return {
      getPaginatedQueryParameters,
      setGetPaginatedQueryParameters,
      getPaginatedLimitList: TABLE_LIMITS_OPTIONS,
      columnOptions,
      setColumnOptions,
      // setGetPaginatedLimitList,
    };
  }, [
    getPaginatedQueryParameters,
    setGetPaginatedQueryParameters,
    columnOptions,
    setColumnOptions,
    // setGetPaginatedLimitList,
  ]);

  return (
    <TariffListContext.Provider value={data}>
      {children}
    </TariffListContext.Provider>
  );
};

// /////////////////////////////////////////////////////////////////////////////

export const FilterTariffListContext =
  createContext<DeepPartialSkipArrayKey<GetTariffsPaginatedModel> | null>(null);

export const useFilterTariffListContext =
  (): DeepPartialSkipArrayKey<GetTariffsPaginatedModel> => {
    const context = useContext(FilterTariffListContext);

    if (!context) {
      throw new Error(
        'useFilterTariffListContext must be used within FilterTariffListContext.Provider',
      );
    }

    return context;
  };

interface FilterTariffListContextProviderProps {
  data: DeepPartialSkipArrayKey<GetTariffsPaginatedModel>;
  children: React.ReactNode;
}

export const FilterTariffListContextProvider = ({
  data,
  children,
}: FilterTariffListContextProviderProps): React.JSX.Element => {
  return (
    <FilterTariffListContext.Provider value={data}>
      {children}
    </FilterTariffListContext.Provider>
  );
};

// /////////////////////////////////////////////////////////////////////////////

export const FilterSearchTariffListContext =
  createContext<DeepPartialSkipArrayKey<GetTariffsPaginatedModel> | null>(null);

export const useFilterSearchTariffListContext =
  (): DeepPartialSkipArrayKey<GetTariffsPaginatedModel> => {
    const context = useContext(FilterSearchTariffListContext);

    if (!context) {
      throw new Error(
        'useFilterSearchTariffListContext must be used within FilterSearchTariffListContext.Provider',
      );
    }

    return context;
  };

interface FilterSearchTariffListContextProviderProps {
  data: DeepPartialSkipArrayKey<GetTariffsPaginatedModel>;
  children: React.ReactNode;
}

export const FilterSearchTariffListContextProvider = ({
  data,
  children,
}: FilterSearchTariffListContextProviderProps): React.JSX.Element => {
  return (
    <FilterSearchTariffListContext.Provider value={data}>
      {children}
    </FilterSearchTariffListContext.Provider>
  );
};

// /////////////////////////////////////////////////////////////////////////////

export const CreateTariffContext =
  createContext<DeepPartialSkipArrayKey<CreateTariffModel> | null>(null);

export const useCreateTariffContext =
  (): DeepPartialSkipArrayKey<CreateTariffModel> => {
    const context = useContext(CreateTariffContext);

    if (!context) {
      throw new Error(
        'useCreateTariffContext must be used within the CreateTariffContext',
      );
    }

    return context;
  };

interface CreateTariffContextProviderProps {
  data: DeepPartialSkipArrayKey<CreateTariffModel>;
  children: React.ReactNode;
}

export const CreateTariffContextProvider = ({
  data,
  children,
}: CreateTariffContextProviderProps): React.JSX.Element => {
  return (
    <CreateTariffContext.Provider value={data}>
      {children}
    </CreateTariffContext.Provider>
  );
};

// /////////////////////////////////////////////////////////////////////////////

export const UpdateTariffContext = createContext<UpdateTariffModel | null>(
  null,
);

export const useUpdateTariffContext = (): UpdateTariffModel => {
  const context = useContext(UpdateTariffContext);

  if (!context) {
    throw new Error(
      'useUpdateTariffContext must be used within UpdateTariffContext.Provider',
    );
  }

  return context;
};

interface UpdateTariffContextProviderProps {
  data: UpdateTariffModel;
  children: React.ReactNode;
}

export const UpdateTariffContextProvider = ({
  data,
  children,
}: UpdateTariffContextProviderProps): React.JSX.Element => {
  return (
    <UpdateTariffContext.Provider value={data}>
      {children}
    </UpdateTariffContext.Provider>
  );
};

// /////////////////////////////////////////////////////////////////////////////

export const DeleteTariffContext = createContext<Pick<
  DeleteTariffModel,
  'id'
> | null>(null);

export const useDeleteTariffContext = (): Pick<DeleteTariffModel, 'id'> => {
  const context = useContext(DeleteTariffContext);

  if (!context) {
    throw new Error(
      'useDeleteTariffContext must be used within DeleteTariffContext.Provider',
    );
  }

  return context;
};

interface DeleteTariffContextProviderProps {
  data: Pick<DeleteTariffModel, 'id'>;
  children: React.ReactNode;
}

export const DeleteTariffContextProvider = ({
  data,
  children,
}: DeleteTariffContextProviderProps): React.JSX.Element => {
  return (
    <DeleteTariffContext.Provider value={data}>
      {children}
    </DeleteTariffContext.Provider>
  );
};

// /////////////////////////////////////////////////////////////////////////////

export type SimplePriceComponentModel = Omit<PriceComponentModel, 'type'>;

export interface TimeSlotModel {
  startTime?: string;
  endTime?: string;
  hasParkingPrice: boolean;
}

export interface RangeUIModel {
  daysOfWeek?: DayOfWeek[];
  hasDoubleTimeSlot: boolean;
  firstTimeSlot: TimeSlotModel;
  secondTimeSlot: TimeSlotModel;
}

export interface TariffUIState {
  isPriceRange: boolean;
  flatValue?: number;
  firstRange: RangeUIModel;
  secondRange: RangeUIModel;

  setIsPriceRange: (value: boolean) => void;
  setFlatValue: (value: number | undefined) => void;
  setFirstRange: (value: RangeUIModel) => void;
  setSecondRange: (value: RangeUIModel) => void;
}

const TariffUIContext = createContext<TariffUIState | null>(null);

export const useTariffUIContext = (): TariffUIState => {
  const context = useContext(TariffUIContext);
  if (!context) {
    throw new Error('useTariffUIContext must be used within TariffUIProvider');
  }
  return context;
};

export const TariffUIProvider = ({
  children,
}: {
  children: React.ReactNode;
}): React.JSX.Element => {
  const emptySlot = {
    energy: {},
    hasParkingPrice: false,
  };

  const [isPriceRange, setIsPriceRange] = useState(false);
  const [flatValue, setFlatValue] = useState<number>();
  const [firstRange, setFirstRange] = useState<RangeUIModel>({
    hasDoubleTimeSlot: false,
    firstTimeSlot: { ...emptySlot },
    secondTimeSlot: { ...emptySlot },
  });
  const [secondRange, setSecondRange] = useState<RangeUIModel>({
    hasDoubleTimeSlot: false,
    firstTimeSlot: { ...emptySlot },
    secondTimeSlot: { ...emptySlot },
  });

  const value = useMemo(
    () => ({
      isPriceRange,
      setIsPriceRange,
      flatValue,
      setFlatValue,
      firstRange,
      setFirstRange,
      secondRange,
      setSecondRange,
    }),
    [isPriceRange, flatValue, firstRange, secondRange],
  );

  return (
    <TariffUIContext.Provider value={value}>
      {children}
    </TariffUIContext.Provider>
  );
};
